import { useMutation, useQuery, useQueryClient } from '@tanstack/vue-query'
import { http, unwrap } from './client'
import type { ApiEnvelope, AuthenticatedUser } from '@/types/api'

export const authKeys = {
  me: ['auth', 'me'] as const,
}

export const useCurrentUser = () =>
  useQuery({
    queryKey: authKeys.me,
    queryFn: async () => {
      const { data } = await http.get<ApiEnvelope<AuthenticatedUser>>('/auth/me')
      return unwrap(data)
    },
    retry: false,
    staleTime: 5 * 60_000,
  })

export const useRequestMagicLink = () =>
  useMutation({
    mutationFn: async (email: string) => {
      const { data } = await http.post<ApiEnvelope<{ sent: true }>>('/auth/login', { email })
      return unwrap(data)
    },
  })

export const useConsumeMagicLink = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (token: string) => {
      const { data } = await http.post<ApiEnvelope<{ authenticated: true }>>('/auth/callback', {
        token,
      })
      return unwrap(data)
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: authKeys.me }),
  })
}

/**
 * The second way in. Same single-use request as the link, so whichever the
 * user reaches for first wins.
 */
export const useVerifyOtp = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ email, code }: { email: string; code: string }) => {
      const { data } = await http.post<ApiEnvelope<{ authenticated: true }>>('/auth/verify-otp', {
        email,
        code,
      })
      return unwrap(data)
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: authKeys.me }),
  })
}

/**
 * The user's own name, function and language. The response is the full /me
 * shape, so the caller replaces the store's copy rather than patching it.
 */
export const useUpdateProfile = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (changes: {
      name?: string
      function?: string
      locale?: string | null
      preferredName?: string
    }) => {
      const { data } = await http.patch<ApiEnvelope<AuthenticatedUser>>('/auth/me', changes)
      return unwrap(data)
    },
    onSuccess: (user) => queryClient.setQueryData(authKeys.me, user),
  })
}

/** The signed-in user's own picture. Both answer with the refreshed /me, so the shell updates at once. */
const refreshMe = async (): Promise<AuthenticatedUser> =>
  unwrap((await http.get<ApiEnvelope<AuthenticatedUser>>('/auth/me')).data)

export const useSetAvatar = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (file: File) => {
      const form = new FormData()
      form.append('file', file)
      // Let the browser write the multipart boundary (and the Content-Type).
      await http.put('/auth/me/avatar', form, { headers: { 'Content-Type': undefined } })
      return refreshMe()
    },
    onSuccess: (user) => queryClient.setQueryData(authKeys.me, user),
  })
}

export const useRemoveAvatar = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async () => {
      await http.delete('/auth/me/avatar')
      return refreshMe()
    },
    onSuccess: (user) => queryClient.setQueryData(authKeys.me, user),
  })
}

/**
 * "View as" (US-21). Both calls end in a full reload rather than a cache
 * invalidation: every store, query and loaded locale on the page belongs to
 * the identity that is going away, and a reload is the one way to be sure
 * nothing of it survives.
 */
export const useStartViewAs = () =>
  useMutation({
    mutationFn: async (userId: string) => {
      const { data } = await http.post<ApiEnvelope<{ viewingAs: string }>>('/admin/impersonation', {
        userId,
      })
      return unwrap(data)
    },
    onSuccess: () => window.location.assign('/'),
  })

export const useStopViewAs = () =>
  useMutation({
    mutationFn: async () => {
      const { data } = await http.delete<ApiEnvelope<{ viewingAs: null }>>('/admin/impersonation')
      return unwrap(data)
    },
    onSuccess: () => window.location.assign('/'),
  })

export const useLogout = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async () => {
      const { data } = await http.post<ApiEnvelope<{ loggedOut: true }>>('/auth/logout')
      return unwrap(data)
    },
    onSuccess: () => queryClient.clear(),
  })
}
